import React from 'react';
import { FormattedMessage } from 'react-intl';

import { TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL } from 'src/util/constants';

/**
 * Delivers a link to a property for a trade point, returning null if no link can be generated.
 * @param {object} tradePoint
 * @param {boolean} isPopover
 * @returns {React.ReactElement} - property link.
 */
export const getPropertyLink = (tradePoint, isPopover) => {
  const property = tradePoint?.meter?.property;
  if (!property) {
    return null;
  }

  const { id, title } = property;
  const label = !title ? id : title;

  if (!label || label === '') {
    return null;
  }

  if (isPopover) {
    return <strong>{label}</strong>;
  }

  if (id) {
    const redirectLink = `/properties/${id}`;

    return (
      <span>
        <a href={redirectLink}>{label}</a>
      </span>
    );
  }

  return <span>{label}</span>;
};

/**
 * Returns a class name when there are no trade rules.
 * Used for targeting components while launching product tours
 * @param {object} tradeRules
 * @param {string} tradeType
 * @returns {string} - class name.
 */
export const emptyTradeRulesClassName = (tradeRules, tradeType) => {
  if (tradeRules && tradeType) {
    const { buy, sell } = tradeRules;
    if (buy?.length === 0 && sell?.length === 0) {
      return ` no-trade-rules-${tradeType}`;
    }
  }
  return '';
};

/**
 * Returns a class name when there are any buy trade rule to prioritize.
 * Used for targeting components while launching product tours
 * @param {object} tradeRules
 * @param {string} tradeType
 * @returns {string} - class name.
 */
export const prioritizeTradesClassName = (tradeRules, tradeType) => {
  if (tradeRules && tradeType) {
    const { buy } = tradeRules;
    if (buy?.length > 0) {
      return ` prioritize-trade-rules-${tradeType}`;
    }
  }
  return '';
};

/**
 * Build a label for a trade rule meter.
 * @param {object} trader
 * @param {object} counterparty
 * @param {string} direction
 * @param {boolean} isPopover
 * @returns {React.ReactElement} - trade rule meter label.
 */
export const getTradeRuleMeterLabel = (
  trader,
  counterparty,
  direction,
  isPopover,
) => {
  if (!trader) {
    return null;
  }
  const { tradePoint } = trader;
  const { id: tradePointId, meter } = tradePoint || {};
  const { id: counterpartyTradepointId } = counterparty?.tradePoint || {};

  const counterPartyCheck = tradePointId === counterpartyTradepointId;
  const finalCounterparty = counterPartyCheck ? '' : counterparty;

  const { title, identifier } = meter || {};
  const propertyLink = getPropertyLink(tradePoint, isPopover);
  const {
    title: counterpartyTitle,
    identifier: counterpartyIdentifier,
  } = finalCounterparty?.tradePoint?.meter || {};
  const { tradePoint: counterpartyTradePoint } = finalCounterparty || {};
  const counterpartyPropertyLabel = getPropertyLink(counterpartyTradePoint, isPopover);
  const counterpartyMeterExist = counterpartyTitle && counterpartyIdentifier;

  if (meter) {
    if (!propertyLink) {
      return (
        <FormattedMessage
          id="helpers.trade_helpers.get_trade_rule_meter_label.no_property.label"
          defaultMessage="{title} ({identifier})"
          values={{
            identifier,
            title,
          }}
        />
      );
    }
    if (propertyLink && !counterpartyMeterExist) {
      return (
        <FormattedMessage
          id="helpers.trade_helpers.get_trade_rule_meter_label.with_property.label"
          defaultMessage="{title} ({identifier}) at {propertyLink}"
          values={{
            identifier,
            propertyLink,
            title,
          }}
        />
      );
    }
    if (propertyLink && counterpartyMeterExist && !counterpartyPropertyLabel) {
      if (direction === TRADE_DIRECTION_BUY) {
        return (
          <FormattedMessage
            id="helpers.trade_helpers.get_trade_rule_meter_label.trade_direction_buy.with_property_and_counterparty_meter.label"
            defaultMessage="{title} ({identifier}) at {propertyLink} from {counterpartyTitle} ({counterpartyIdentifier})"
            values={{
              counterpartyIdentifier,
              counterpartyTitle,
              identifier,
              propertyLink,
              title,
            }}
          />
        );
      }
      if (direction === TRADE_DIRECTION_SELL) {
        return (
          <FormattedMessage
            id="helpers.trade_helpers.get_trade_rule_meter_label.trade_direction_sell.with_property_and_counterparty_meter.label"
            defaultMessage="{title} ({identifier}) at {propertyLink} to {counterpartyTitle} ({counterpartyIdentifier})"
            values={{
              counterpartyIdentifier,
              counterpartyTitle,
              identifier,
              propertyLink,
              title,
            }}
          />
        );
      }
    }
    if (propertyLink && counterpartyMeterExist && counterpartyPropertyLabel) {
      if (direction === TRADE_DIRECTION_BUY) {
        return (
          <FormattedMessage
            id="helpers.trade_helpers.get_trade_rule_meter_label.trade_direction_buy.with_property_and_counterparty_meter_and_counterparty_property.label"
            defaultMessage="{title} ({identifier}) at {propertyLink} from {counterpartyTitle} ({counterpartyIdentifier}) at {counterpartyPropertyLabel}"
            values={{
              counterpartyIdentifier,
              counterpartyTitle,
              counterpartyPropertyLabel,
              identifier,
              propertyLink,
              title,
            }}
          />
        );
      }
      if (direction === TRADE_DIRECTION_SELL) {
        return (
          <FormattedMessage
            id="helpers.trade_helpers.get_trade_rule_meter_label.trade_direction_sell.with_property_and_counterparty_meter_and_counterparty_property.label"
            defaultMessage="{title} ({identifier}) at {propertyLink} to {counterpartyTitle} ({counterpartyIdentifier}) at {counterpartyPropertyLabel}"
            values={{
              counterpartyIdentifier,
              counterpartyTitle,
              counterpartyPropertyLabel,
              identifier,
              propertyLink,
              title,
            }}
          />
        );
      }
    }
  }
  return null;
};
